import { Route, Redirect, useHistory, useLocation } from 'react-router-dom'
import { cloneElement, useState } from 'react'
import { useSelector } from 'react-redux'
import { get, some } from 'lodash'
import Sidebar from 'containers/Sidebar'
import MobileHeader from 'containers/MobileHeader'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { getPostLoginPath } from 'lib/auth/dashboardRole'

/**
 * @isPublic - can public see this page without login?
 * @isUser - is user logged in?
 * @WhatItDoes - This component will return user to login page if they are not logged in.
 * and if they are logged in then it will just return the passed component
 */

/**
 * Renders sidebar + mobile header once; children are the active route’s page.
 * Used as the shell for all authenticated routes so Sidebar does not remount on navigation.
 */
export const AuthenticatedShell = ({ children, showSideBar = true, openChangePasswordModal }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const drawer = useSelector(state => get(state, 'settings.drawer'))
  const user = useSelector(state => get(state, 'user.user'))
  const { i18n } = useTranslation()
  const isRTL = i18n.language === 'ar'
  const history = useHistory()
  const isBookingsPage = history.location.pathname === '/bookings'

  const showSidebar = true
  const paddingClass = isBookingsPage ? (isRTL ? 'sidebar:pr-64' : 'sidebar:pl-64') : isRTL ? 'lg:pr-64' : 'lg:pl-64'

  return (
    <div>
      {showSideBar && showSidebar ? <Sidebar user={user} setVisible={setSidebarOpen} visible={sidebarOpen} openChangePasswordModal={openChangePasswordModal} /> : null}
      <div className={showSideBar ? classNames(drawer && showSidebar && paddingClass, 'flex cubic-transition flex-col') : classNames()}>
        {showSideBar && showSidebar ? <MobileHeader user={user} openChangePasswordModal={openChangePasswordModal} setSidebarOpen={setSidebarOpen} /> : null}
        {children}
      </div>
    </div>
  )
}

const AddSlider = ({ child, showSideBar, readOnly, openChangePasswordModal }) => {
  return (
    <AuthenticatedShell showSideBar={showSideBar} openChangePasswordModal={openChangePasswordModal}>
      {child}
    </AuthenticatedShell>
  )
}

const AuthRoute = ({
  /** When set, logged-in users hitting a public route go here instead of the role default. */
  customRedirect,
  readOnly,
  isPublic = false,
  showSideBar = true,
  children,
  isUser,
  component: Component = null,
  exact,
  path,
  openChangePasswordModal,
  ...rest
}) => {
  const user = useSelector(state => get(state, 'user.user'))
  const location = useLocation()
  const defaultLoggedInPublicRedirect = getPostLoginPath(user)
  const loggedInPublicRedirect = customRedirect ?? defaultLoggedInPublicRedirect
  const hasPasswordResetToken = location.pathname === '/forgot' && new URLSearchParams(location.search).has('token')

  const onPublic = () => {
    // Password setup/reset magic links must remain reachable while logged in
    if (isUser && !hasPasswordResetToken) {
      return <Redirect to={loggedInPublicRedirect} />
    } else {
      return Component ? (
        <AddSlider child={cloneElement(Component, { readOnly })} showSideBar={showSideBar} openChangePasswordModal={openChangePasswordModal} />
      ) : (
        <AddSlider child={children} showSideBar={showSideBar} openChangePasswordModal={openChangePasswordModal} />
      )
    }
  }
  const onPrivate = () => {
    if (isUser) {
      return Component ? (
        <AddSlider child={cloneElement(Component, { readOnly })} showSideBar={showSideBar} openChangePasswordModal={openChangePasswordModal} />
      ) : (
        <AddSlider child={children} showSideBar={showSideBar} openChangePasswordModal={openChangePasswordModal} />
      )
    } else {
      return <Redirect to="/login" />
    }
  }

  if (isPublic) {
    return <Route {...rest} exact={exact} path={path} render={() => onPublic()} />
  } else {
    return <Route {...rest} exact={exact} path={path} render={() => onPrivate()} />
  }
}

export default AuthRoute
